import { cn } from '@/platform/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

const getPages = (currentPage, totalPages, siblings) => {
    const totalNumbers = siblings * 2 + 5;

    if (totalPages <= totalNumbers) {
        return range(1, totalPages);
    }

    const left = Math.max(currentPage - siblings, 2);
    const right = Math.min(currentPage + siblings, totalPages - 1);

    const showLeftDots = left > 2;
    const showRightDots = right < totalPages - 1;

    const pages = [1];

    if (showLeftDots) {
        pages.push('left-dots');
    }

    pages.push(...range(left, right));

    if (showRightDots) {
        pages.push('right-dots');
    }

    pages.push(totalPages);

    return pages;
};

const itemClasses =
    'inline-flex h-9 min-w-9 cursor-pointer items-center justify-center px-3 text-sm font-medium transition';

const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    siblings = 2,
    onPageChange,
    className,
    ...props
}) => {
    if (!totalPages || totalPages < 2) {
        return null;
    }

    const goTo = page => {
        if (page < 1 || page > totalPages || page === currentPage) {
            return;
        }

        onPageChange?.(page);
    };

    return (
        <nav
            aria-label='Pagination'
            className={cn(
                'inline-flex items-center divide-x divide-gray-200 overflow-hidden rounded-md border border-gray-200',
                className
            )}
            {...props}
        >
            <button
                type='button'
                onClick={() => goTo(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label='Previous page'
                className={cn(
                    itemClasses,
                    'hover:bg-primary-50 hover:text-primary-700 text-gray-400 disabled:pointer-events-none disabled:opacity-40'
                )}
            >
                <ChevronLeftIcon className='size-5' />
            </button>

            {getPages(currentPage, totalPages, siblings).map((page, index) =>
                typeof page === 'string' ? (
                    <span
                        key={`${page}-${index}`}
                        className={cn(
                            itemClasses,
                            'pointer-events-none text-gray-400'
                        )}
                    >
                        &hellip;
                    </span>
                ) : (
                    <button
                        key={page}
                        type='button'
                        onClick={() => goTo(page)}
                        aria-current={page === currentPage ? 'page' : undefined}
                        className={cn(
                            itemClasses,
                            page === currentPage
                                ? 'bg-primary-600 text-white'
                                : 'hover:bg-primary-50 hover:text-primary-700 text-gray-700'
                        )}
                    >
                        {page}
                    </button>
                )
            )}

            <button
                type='button'
                onClick={() => goTo(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label='Next page'
                className={cn(
                    itemClasses,
                    'hover:bg-primary-50 hover:text-primary-700 text-gray-400 disabled:pointer-events-none disabled:opacity-40'
                )}
            >
                <ChevronRightIcon className='size-5' />
            </button>
        </nav>
    );
};

export default Pagination;
